// TrendLine — an honest, hand-authored line chart for narrated heroes (e.g. the
// trailing-12-month net-income trend). Inline SVG, no chart libraries. The last
// point is emphasized and colored by sign (clay when negative, petrol
// otherwise) so the headline number reads at a glance.
//
// When `interactive`, each data point reveals a value tooltip on hover. A click
// resolves to the chart in general (`onSelect({kind:'chart'})`) — time-series
// points are hover-only, never selected, per the interaction model.

import { useState, type MouseEvent } from 'react'
import { CfoChartFrame, useChartTooltip, type CfoChartSelection } from './chartInteraction'

export type TrendPoint = { label?: string; value: number; low?: number | null; high?: number | null }
type TrendHover = { kind: 'mid' | 'low' | 'high'; index: number } | null

/**
 * A line drawn on the same geometry as the series but NOT of it — an operating
 * target, a fitted trend, a reference line. Deliberately not a second `points`
 * array: it gets no dot, no area fill and no hover, because it is a commitment
 * or a computation rather than a measured value and must never read as one.
 *
 * `values` align index-for-index with `points`; a null breaks the line there
 * rather than interpolating across a period the reference does not cover.
 */
export type TrendReferenceSeries = {
  values: Array<number | null>
  /** A shaded span around the line (a standard-deviation band). */
  band?: { upper: Array<number | null>; lower: Array<number | null> } | null
  /** Drawn at the last segment that has a value. Omit for an unlabeled rule. */
  label?: string
  /** Extra class on the group (e.g. an optimistic-write modifier). */
  className?: string
  /** Stroke colour. Defaults to anchor grey — always subordinate to the series. */
  stroke?: string
  /** Dash pattern. Defaults to "6 4". */
  dash?: string
  strokeWidth?: number
}

export type TrendLineProps = {
  points: TrendPoint[]
  ariaLabel: string
  /** Tick labels under the axis (sparse, e.g. ["Jul","Oct","Jan","Apr","Jun"]). */
  axisLabels?: string[]
  /** Format the emphasized last value annotation (and hover tooltips). */
  formatValue?: (value: number) => string
  /** Also label the first point. Useful when start/end values matter. */
  showStartValue?: boolean
  /** Enable hover value tooltips on each point. */
  interactive?: boolean
  /** Click resolves to the chart in general (points are hover-only). */
  onSelect?: (selection: CfoChartSelection) => void
  /** Dashed references drawn on the same geometry (target / fit / reference lines). */
  referenceSeries?: TrendReferenceSeries[] | null
  width?: number
  height?: number
  className?: string
}

export type TrendLineGeometry = {
  linePoints: Array<{ x: number; y: number }>
  zeroY: number
  min: number
  max: number
}

/** Compute line geometry mapped into the plot box. Exported for tests. */
export function computeTrendGeometry(
  values: number[],
  width: number,
  height: number,
  padX = 60,
  padTop = 24,
  padBottom = 36,
  domainValues: number[] = values,
): TrendLineGeometry {
  const finite = domainValues.filter((v) => Number.isFinite(v))
  const rawMin = finite.length ? Math.min(...finite, 0) : 0
  const rawMax = finite.length ? Math.max(...finite, 0) : 1
  const min = rawMin
  const max = rawMax === rawMin ? rawMin + 1 : rawMax
  const span = max - min || 1
  const plotW = width - padX - 12
  const plotH = height - padTop - padBottom
  const stepX = values.length > 1 ? plotW / (values.length - 1) : 0
  const toY = (v: number) => padTop + (1 - (v - min) / span) * plotH
  const linePoints = values.map((v, i) => ({
    x: padX + i * stepX,
    y: toY(Number.isFinite(v) ? v : min),
  }))
  return { linePoints, zeroY: toY(0), min, max }
}

export function TrendLine({
  points,
  ariaLabel,
  axisLabels,
  formatValue,
  showStartValue = false,
  interactive = false,
  onSelect,
  referenceSeries = null,
  width = 920,
  height = 260,
  className,
}: TrendLineProps) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const [hover, setHover] = useState<TrendHover>(null)
  const values = points.map((p) => p.value)
  const bandValues = points.flatMap((p) => [p.low, p.high]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  // The reference joins the vertical domain so a target well outside the
  // series is still on-canvas — a target you cannot see is not a target.
  const referenceValues = (referenceSeries || [])
    .flatMap((reference) => [
      ...reference.values,
      ...(reference.band?.upper || []),
      ...(reference.band?.lower || []),
    ])
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  const { linePoints, zeroY, min, max } = computeTrendGeometry(values, width, height, 60, 24, 36, [...values, ...bandValues, ...referenceValues])
  const fmt = (v: number) => (formatValue ? formatValue(v) : String(Math.round(v)))
  if (linePoints.length < 2) {
    return (
      <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        <text x={width / 2} y={height / 2} textAnchor="middle" style={{ fill: 'var(--muted)', font: '500 13px var(--font-ui)' }}>
          Not enough history to chart yet
        </text>
      </svg>
    )
  }
  const last = linePoints[linePoints.length - 1]
  const lastValue = values[values.length - 1]
  const negative = lastValue < 0
  const path = linePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
  const plotW = width - 60 - 12
  const plotH = height - 24 - 36
  const toY = (value: number) => 24 + (1 - (value - min) / (max - min || 1)) * plotH
  const stepX = points.length > 1 ? plotW / (points.length - 1) : 0
  const bandPoints = (() => {
    const usable = points.every((point) => Number.isFinite(point.low) && Number.isFinite(point.high))
    if (!usable) return null
    const highs = points.map((point, i) => ({
      x: 60 + i * stepX,
      y: toY(point.high as number),
    }))
    const lows = points.map((point, i) => ({
      x: 60 + i * stepX,
      y: toY(point.low as number),
    }))
    return { highs, lows }
  })()
  const bandPath = bandPoints
    ? [
        ...bandPoints.highs.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
        ...bandPoints.lows.slice().reverse().map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
        'Z',
      ].join(' ')
    : null
  const highPath = bandPoints?.highs.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') || null
  const lowPath = bandPoints?.lows.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') || null
  // Reference runs break at every null rather than interpolating across a
  // period the reference does not cover. A lone point still draws, as a short
  // rule, so a single-period commitment is never invisible.
  const referenceRuns = (series: Array<number | null>): Array<Array<{ x: number; y: number }>> => {
    const segments: Array<Array<{ x: number; y: number }>> = []
    let run: Array<{ x: number; y: number }> = []
    linePoints.forEach((point, i) => {
      const value = series[i]
      if (typeof value === 'number' && Number.isFinite(value)) {
        run.push({ x: point.x, y: toY(value) })
        return
      }
      if (run.length) segments.push(run)
      run = []
    })
    if (run.length) segments.push(run)
    return segments
  }
  const runsToPath = (segments: Array<Array<{ x: number; y: number }>>): string | null =>
    segments
      .map((segment) =>
        segment.length === 1
          ? `M ${(segment[0].x - 5).toFixed(1)} ${segment[0].y.toFixed(1)} L ${(segment[0].x + 5).toFixed(1)} ${segment[0].y.toFixed(1)}`
          : segment.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' '),
      )
      .join(' ') || null
  const references = (referenceSeries || []).map((reference) => {
    const segments = referenceRuns(reference.values)
    const upperRuns = reference.band ? referenceRuns(reference.band.upper) : []
    const lowerRuns = reference.band ? referenceRuns(reference.band.lower) : []
    // A band is only drawn where BOTH bounds exist across the same run, so a
    // partially covered band never gets closed against a bound it lacks.
    const bandPathFor =
      upperRuns.length === 1 && lowerRuns.length === 1 && upperRuns[0].length === lowerRuns[0].length
        ? [
            ...upperRuns[0].map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
            ...lowerRuns[0].slice().reverse().map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
            'Z',
          ].join(' ')
        : null
    return {
      reference,
      path: runsToPath(segments),
      bandPath: bandPathFor,
      end: segments.length ? segments[segments.length - 1].slice(-1)[0] : null,
    }
  })
  const nearestIndexFromClientX = (clientX: number, svg: SVGSVGElement | null): number => {
    const rect = svg?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return 0
    const x = ((clientX - rect.left) / rect.width) * width
    return linePoints.reduce((best, point, idx) => (
      Math.abs(point.x - x) < Math.abs(linePoints[best].x - x) ? idx : best
    ), 0)
  }
  const showBoundTip = (event: MouseEvent<SVGPathElement>, kind: 'low' | 'high') => {
    const index = nearestIndexFromClientX(event.clientX, event.currentTarget.ownerSVGElement)
    const value = kind === 'high' ? points[index].high : points[index].low
    if (!Number.isFinite(value)) return
    setHover({ kind, index })
    show(event.clientX, event.clientY, fmt(value as number), `${kind === 'high' ? 'Upper' : 'Lower'} bound${points[index].label ? ` · ${points[index].label}` : ''}`)
  }
  const area =
    `${path} L ${last.x.toFixed(1)} ${(height - 36).toFixed(1)} L ${linePoints[0].x.toFixed(1)} ${(height - 36).toFixed(1)} Z`
  const tickX = (i: number) => linePoints[Math.round((i / Math.max(1, (axisLabels?.length || 1) - 1)) * (linePoints.length - 1))]?.x ?? 0
  const hitRadius = Math.max(12, (linePoints[1]?.x ?? 24) - (linePoints[0]?.x ?? 0)) / 2
  const svg = (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      <line x1={60} y1={zeroY} x2={width - 12} y2={zeroY} stroke="var(--axis, #3a3f47)" strokeWidth={1.2} />
      {bandPath ? <path d={bandPath} fill="var(--pos, #0e7c86)" opacity={0.11} /> : null}
      <path d={area} fill="var(--pos, #0e7c86)" opacity={0.07} />
      {/* Drawn UNDER the series: where a commitment and a measured value
          coincide, the measured value is the one that must read. */}
      {references.map(({ reference, path: refPath, bandPath: refBand, end }, refIndex) =>
        refPath || refBand ? (
          <g
            key={`ref-${refIndex}`}
            className={`cfo-trend-reference${reference.className ? ` ${reference.className}` : ''}`}
          >
            {refBand ? (
              <path d={refBand} fill={reference.stroke || 'var(--anchor, #9aa0a8)'} opacity={0.1} />
            ) : null}
            {refPath ? (
              <path
                d={refPath}
                fill="none"
                stroke={reference.stroke || 'var(--anchor, #9aa0a8)'}
                strokeWidth={reference.strokeWidth ?? 2}
                strokeDasharray={reference.dash || '6 4'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
            {reference.label && end ? (
              <text
                x={Math.min(end.x, width - 14)}
                y={end.y - 7}
                textAnchor="end"
                style={{ font: '700 11px var(--font-ui)', fill: reference.stroke || 'var(--ink-3, #6b717c)' }}
              >
                {reference.label}
              </text>
            ) : null}
          </g>
        ) : null,
      )}
      <path d={path} fill="none" stroke="var(--pos, #0e7c86)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r={5} fill={negative ? 'var(--neg, #c0492f)' : 'var(--pos, #0e7c86)'} />
      {formatValue ? (
        <>
          {showStartValue ? (
            <text
              x={linePoints[0].x}
              y={linePoints[0].y - 14}
              textAnchor="start"
              style={{ font: '700 12px var(--font-ui)', fill: values[0] < 0 ? 'var(--neg-ink, #a83e29)' : 'var(--pos, #0e7c86)' }}
            >
              {formatValue(values[0])}
            </text>
          ) : null}
          <text
            x={last.x}
            y={last.y + 22}
            textAnchor="end"
            style={{ font: '700 12px var(--font-ui)', fill: negative ? 'var(--neg-ink, #a83e29)' : 'var(--pos, #0e7c86)' }}
          >
            {formatValue(lastValue)}
          </text>
        </>
      ) : null}
      {axisLabels && axisLabels.length > 0 ? (
        <g style={{ font: 'italic 500 11px var(--font-guide)', fill: 'var(--muted)' }}>
          {axisLabels.map((label, i) => (
            <text key={`${label}-${i}`} x={tickX(i)} y={height - 12} textAnchor="middle">
              {label}
            </text>
          ))}
        </g>
      ) : null}
      {interactive
        ? linePoints.map((p, i) => (
            <g key={`hit-${i}`}>
              {hover?.kind === 'mid' && hover.index === i ? (
                <circle cx={p.x} cy={p.y} r={5.5} fill="none" stroke="var(--pos, #0e7c86)" strokeWidth={2} />
              ) : null}
              <circle
                className="cfo-chart-hit"
                cx={p.x}
                cy={p.y}
                r={hitRadius}
                fill="transparent"
                onMouseEnter={(e) => {
                  setHover({ kind: 'mid', index: i })
                  show(e.clientX, e.clientY, fmt(values[i]), points[i].label || '')
                }}
                onMouseMove={(e) => show(e.clientX, e.clientY, fmt(values[i]), points[i].label || '')}
                onMouseLeave={() => {
                  setHover(null)
                  hide()
                }}
              />
            </g>
          ))
        : null}
      {interactive && highPath ? (
        <path
          className="cfo-chart-band-hit"
          d={highPath}
          fill="none"
          stroke="rgba(14, 124, 134, 0.001)"
          strokeWidth={18}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents="stroke"
          onMouseEnter={(event) => showBoundTip(event, 'high')}
          onMouseMove={(event) => showBoundTip(event, 'high')}
          onMouseLeave={() => {
            setHover(null)
            hide()
          }}
        />
      ) : null}
      {interactive && lowPath ? (
        <path
          className="cfo-chart-band-hit"
          d={lowPath}
          fill="none"
          stroke="rgba(14, 124, 134, 0.001)"
          strokeWidth={18}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents="stroke"
          onMouseEnter={(event) => showBoundTip(event, 'low')}
          onMouseMove={(event) => showBoundTip(event, 'low')}
          onMouseLeave={() => {
            setHover(null)
            hide()
          }}
        />
      ) : null}
    </svg>
  )
  if (!interactive) return svg
  return (
    <CfoChartFrame containerRef={containerRef} tip={tip} className={className ? `${className}-frame` : undefined}>
      <div
        onClick={onSelect ? () => onSelect({ kind: 'chart' }) : undefined}
        onMouseLeave={() => {
          setHover(null)
          hide()
        }}
      >
        {svg}
      </div>
    </CfoChartFrame>
  )
}

export default TrendLine
